import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Pill, Spinner } from "../components/ui";
import AppHeader from "../components/AppHeader";
import { Phone, MessageCircle, Mail, Calendar, FileText } from "lucide-react";
import { formatDate } from "../lib/dates";

const STATUS_OPTIONS = [
  { key: "unconverted", label: "Unconverted" },
  { key: "converted", label: "Converted" },
  { key: "regular_production", label: "Regular Production" },
];

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [hist, setHist] = useState({ meetings: [], quotes: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [cust, h] = await Promise.all([api.get(`/customers/${id}`), api.get(`/customers/${id}/history`)]);
      setC(cust); setHist(h);
    } catch (e) { alert(e?.message); navigate(-1); } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading || !c) return <><AppHeader title="Customer" back /><div className="sg-main"><Spinner /></div></>;

  const updateStatus = async (status) => {
    const u = await api.patch(`/customers/${id}`, { status });
    setC(u);
  };

  const tone = c.status === "converted" ? "success" : c.status === "regular_production" ? "brand" : "neutral";
  const wa = (c.whatsapp || c.phone || "").replace(/[^\d]/g, "");

  return (
    <>
      <AppHeader title={c.company_name} back right={<Pill label={c.status} tone={tone} />} />
      <div className="sg-main sg-fade-in">
        <div className="sg-card" style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 13, color: "var(--muted)" }}>{c.brand_name}</div>
          <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 6, fontFamily: "monospace" }}>{c.customer_id}</div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 16 }}>
            <KV label="Contact" v={c.contact_person} />
            <KV label="Phone" v={c.phone} />
            <KV label="Whatsapp" v={c.whatsapp} />
            <KV label="Email" v={c.email} />
            <KV label="GST Status" v={c.gst_status} />
            {c.gst_status === "registered" && <KV label="GST Number" v={c.gst_number} />}
          </div>
          {c.billing_address && <KV label="Billing Address" v={c.billing_address} block />}
          {c.shipping_address && <KV label="Shipping Address" v={c.shipping_address} block />}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
          <a className="sg-btn" href={`tel:${c.phone}`} data-testid="customer-call" style={{ flexDirection: "column", padding: "12px 4px", gap: 4 }}>
            <Phone size={16} color="var(--brand)" />
            <span style={{ fontSize: 11, fontWeight: 700 }}>Call</span>
          </a>
          <a className="sg-btn" href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" data-testid="customer-whatsapp-btn" style={{ flexDirection: "column", padding: "12px 4px", gap: 4 }}>
            <MessageCircle size={16} color="var(--brand)" />
            <span style={{ fontSize: 11, fontWeight: 700 }}>WhatsApp</span>
          </a>
          <a className="sg-btn" href={`mailto:${c.email}`} data-testid="customer-email-btn" style={{ flexDirection: "column", padding: "12px 4px", gap: 4 }}>
            <Mail size={16} color="var(--brand)" />
            <span style={{ fontSize: 11, fontWeight: 700 }}>Email</span>
          </a>
        </div>

        <div className="sg-section-h">Status</div>
        <div className="sg-chip-row" style={{ marginBottom: 12 }}>
          {STATUS_OPTIONS.map((o) => (
            <button
              key={o.key} className={`sg-chip ${c.status === o.key ? "sg-chip-active" : ""}`}
              onClick={() => updateStatus(o.key)}
              data-testid={`set-status-${o.key}`}
            >{o.label}</button>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
          <button className="sg-btn" onClick={() => navigate(`/meetings/new?customer_id=${c.customer_id}&customer_name=${encodeURIComponent(c.company_name)}`)}>
            <Calendar size={14} color="var(--brand)" /> Book Meeting
          </button>
          <button className="sg-btn" onClick={() => navigate(`/quotes/new?customer_id=${c.customer_id}&customer_name=${encodeURIComponent(c.company_name)}`)}>
            <FileText size={14} color="var(--brand)" /> New Quote
          </button>
        </div>

        <div className="sg-section-h">Meetings ({hist.meetings.length})</div>
        {hist.meetings.length === 0 ? <div className="sg-card" style={{ fontSize: 13, color: "var(--muted)" }}>No meetings yet.</div> : (
          hist.meetings.map((m) => (
            <div key={m.meeting_id} className="sg-list-card" onClick={() => navigate(`/meetings/${m.meeting_id}`)}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{formatDate(m.date)} · {m.time}</span>
                <Pill label={m.status} tone={m.status === "completed" ? "success" : "brand"} />
              </div>
              <div style={{ fontSize: 12, color: "var(--muted)" }}>{String(m.meeting_type).replace("_", " ")} · {m.team}</div>
            </div>
          ))
        )}

        <div className="sg-section-h">Quotes ({hist.quotes.length})</div>
        {hist.quotes.length === 0 ? <div className="sg-card" style={{ fontSize: 13, color: "var(--muted)" }}>No quotes yet.</div> : (
          hist.quotes.map((q) => (
            <div key={q.quote_id} className="sg-list-card" onClick={() => navigate(`/quotes/${q.quote_id}`)}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 800, color: "var(--brand)", letterSpacing: 0.4, fontSize: 13 }}>{q.quote_number}</span>
                <Pill label={q.status} tone={q.status === "approved" ? "success" : "neutral"} />
              </div>
              <div style={{ fontSize: 12, color: "var(--muted)" }}>
                {String(q.quote_type).replace("_", " ")} · ₹{Number(q.amount).toLocaleString("en-IN")}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}

function KV({ label, v, block }) {
  return (
    <div style={{ gridColumn: block ? "1 / -1" : undefined, marginTop: block ? 12 : 0 }}>
      <div style={{ fontSize: 10, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 14, color: "var(--ink)", marginTop: 3, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{v || "—"}</div>
    </div>
  );
}
